import {
  Select as MuiSelect,
  MenuItem as MuiMenuItem,
  FormControl,
  OutlinedInput,
  Box,
  styled,
  ListItemIcon,
} from '@mui/material';
import React from 'react';

import { CaretDown, Check, IconProps } from '@phosphor-icons/react';
import type { SelectProps as MuiSelectProps } from '@mui/material';
import type { ComponentType } from 'react';
import { CommonLabelSeparateProps } from './types';
import { getInputStyles } from './utils/getInputStyles';
import { palette } from 'styles/theme/palette';
import { menuItemColor } from './menuItemColor';
import { InputLabel } from 'components/inputs/InputLabel';
import { Chip } from 'components/chip/Chip';
import { Avatar } from 'components/avatar/Avatar';
import { Text, type TextProps } from 'components/text/Text';

export interface SelectProps
  extends Omit<MuiSelectProps, 'variant' | 'placeholder'>,
    CommonLabelSeparateProps {
  variant?: 'standard' | 'filled' | 'outlined' | 'unstyled';
  labelID?: string;
  id?: string;
  options?: Option[];
  actions?: JSX.Element;
  hasAvatar?: boolean;
  placeholder?: React.ReactNode;
  iconOnSelected?: boolean;
}

export const CaretDownIcon = (props: IconProps) => {
  return <CaretDown {...props} size={20} />;
};

export type Option = {
  label: string;
  avatar?: string;
  id: string;
  value?: string;
  customLabel?: string;
  type?: number;
  loginId?: string;
  disableAvatar?: boolean;
  showCustomLabel?: boolean;
  onClick?: (event: React.SyntheticEvent) => void;
};

const DefaultMenuProps = {
  anchorOrigin: {
    vertical: 'bottom' as const,
    horizontal: 'left' as const,
  },
  transformOrigin: {
    vertical: 'top' as const,
    horizontal: 'left' as const,
  },
  slotProps: {
    paper: {
      style: {
        maxHeight: '320px',
        minWidth: 215,
        paddingLeft: '8px',
        paddingRight: '8px',
        marginTop: '4px',
      },
      sx: {
        '& .MuiMenu-list': {
          padding: 0,
        },
      },
    },
  },
};

const SelectBase = ({
  labelID,
  label,
  multiple,
  variant = 'outlined',
  onChange,
  sx,
  className,
  MenuProps,
  options,
  placeholder,
  iconOnSelected = true,
  hasAvatar,
  children,
  actions,
  helperText,
  ...rest
}: SelectProps) => {
  const handleChange: MuiSelectProps['onChange'] = (event, child) => {
    onChange?.(event, child);
  };

  return (
    <FormControl className={className} sx={{ m: 1, width: '100%' }}>
      {label && (
        <InputLabel shrink id={labelID} labelWeight="semiBold">
          {label}
          {rest.required ? <Required> *</Required> : ''}
        </InputLabel>
      )}
      <MuiSelect
        data-testid="select-component"
        IconComponent={CaretDownIcon}
        input={label ? <OutlinedInput label={label} /> : undefined}
        MenuProps={{ ...DefaultMenuProps, ...MenuProps }}
        multiple={multiple}
        displayEmpty
        renderValue={
          options
            ? multiple
              ? renderValues => (
                  <Box sx={{ display: 'flex', gap: '0.25rem', overflow: 'hidden' }}>
                    {(renderValues as string[])?.map(renderValue => {
                      const currentOption = options?.find(option => option.id === renderValue);
                      return <Chip isBordered key={renderValue} label={currentOption?.label} />;
                    })}
                  </Box>
                )
              : selected => {
                  const currentOption = options?.find(option => {
                    return selected === option.id;
                  });

                  return (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        overflow: 'hidden',
                      }}
                    >
                      {!selected && placeholder ? (
                        <Placeholder color={rest.error ? 'error' : undefined}>
                          {placeholder}
                        </Placeholder>
                      ) : null}
                      {hasAvatar && !currentOption?.disableAvatar && (
                        <Avatar
                          size="xs"
                          src={currentOption?.avatar}
                          letters={currentOption?.label}
                        />
                      )}
                      <Text color={rest.error ? 'error' : undefined}>{currentOption?.label}</Text>
                    </Box>
                  );
                }
            : !rest?.value && placeholder
              ? () => {
                  return (
                    <Placeholder color={rest.error ? 'error' : undefined}>
                      {placeholder}
                    </Placeholder>
                  );
                }
              : undefined
        }
        {...rest}
        sx={{
          minHeight: '44px',
          color: menuItemColor,
          ...sx,
        }}
        labelId={labelID}
        variant={variant === 'unstyled' ? 'standard' : variant}
        onChange={handleChange}
        onBlur={e => {
          rest?.onBlur?.(e);
        }}
      >
        {children}
        {options?.map(option => {
          const isSelected = multiple
            ? ((rest?.value ?? []) as string[])?.indexOf(option.id) > -1
            : (rest?.value as string) === option.id;
          return (
            <SelectOption
              avatar={option.avatar}
              disableAvatar={option?.disableAvatar}
              hasAvatar={hasAvatar}
              iconOnSelected={iconOnSelected}
              isSelected={Boolean(isSelected)}
              label={option.label}
              value={option.id}
              key={option.id}
              onClick={option.onClick}
            />
          );
        })}
        {actions && <Actions>{actions}</Actions>}
      </MuiSelect>
      {helperText ? (
        <Text variant="bodySm" color="tertiary">
          {helperText}
        </Text>
      ) : null}
    </FormControl>
  );
};

export const Placeholder = ({ children, color, ...props }: TextProps) => {
  return (
    <Text variant="bodyMd" {...props} color={color ? color : 'placeholder'}>
      {children}
    </Text>
  );
};

const Actions = styled('li')({
  padding: '0. 1rem',
  fontSize: '1rem',
});

const Required = styled('span')(({ theme }) => ({
  color: theme.palette.text.error,
}));

const MenuItem = styled(MuiMenuItem)(({ theme }) => ({
  '&.Mui-selected': {
    backgroundColor: theme.palette.bg.brandSubtle,
    fontWeight: 600,
  },
  marginInline: '6px',
  marginBlock: '2px',
  padding: '6px 10px',
  borderRadius: '4px',
  color: menuItemColor,
}));

const MenuItemContent = styled('div')({
  display: 'flex',
  width: '100%',
  justifyContent: 'flex-start',
  alignItems: 'center',
});

/**
 * Dropdown component, part of Delogue design system.
 * @param options - array of options to select. Each option object contains:
 *   - `label` - the display text for the option
 *   - `id` - unique identifier for the option
 *   - `avatar` - URL or path to avatar image
 *   - `value` - alternative value for the option
 *   - `disableAvatar` - Whether to hide avatar for this option
 *   - `onClick` - Custom click handler for the option
 * @param label - label shown above the dropdown
 * @param onChange - on Change callback function
 * @param placeholder - placeholder text shown inside the dropdown input
 * @param iconOnSelected - show check icon on selection option (default true)
 * @param hasAvatar - show avatar icon on the left of options
 * @param children - JSX shown inside dropdown above the options
 * @param actions - JXS shown inside dropdown below the options
 * @param value - currently selected value
 * @param onBlur - on blur callback function
 * @param error - error state boolean, red texts etc.
 * @param required - show asterix next to label, requires label text
 * @param disabled - read only mode
 */

export const Select: ComponentType<SelectProps> = styled(
  ({
    labelSeparate,
    errorIconProps,
    errorIcon,
    helperText: hint,
    labelVariant,
    labelWeight,
    isUnderline,
    ...styledProps
  }: SelectProps) => {
    const helperText =
      hint && styledProps?.error && errorIcon ? (
        <Box
          sx={{
            p: 0,
            m: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {errorIcon} {hint}
        </Box>
      ) : (
        hint
      );
    return <SelectBase {...styledProps} helperText={helperText} />;
  }
)(({ theme, labelSeparate, labelVariant, isUnderline, labelWeight, labelColor, ...rest }) => {
  return {
    ...getInputStyles({
      theme,
      labelSeparate,
      variant: rest?.variant || 'outlined',
      isUnderline,
      labelColor,
      labelVariant,
      labelWeight,
    }),
  };
});

interface SelectOptionProps {
  isSelected?: boolean;
  hasAvatar?: boolean;
  disableAvatar?: boolean;
  iconOnSelected?: boolean;
  value?: string;
  label?: string;
  avatar?: string;
  onClick?: React.MouseEventHandler<HTMLLIElement>;
}

const SelectOption = ({
  isSelected,
  disableAvatar,
  hasAvatar,
  iconOnSelected,
  value,
  onClick,
  label,
  avatar,
}: SelectOptionProps) => {
  return (
    <MenuItem
      data-testid="menu-item"
      value={value}
      onClick={onClick}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          e.stopPropagation();
        }
      }}
      disableRipple
    >
      <MenuItemContent>
        <div
          style={{
            display: 'flex',
            gap: '0.75rem',
            alignItems: 'center',
            paddingRight: '10px',
          }}
        >
          {hasAvatar && !disableAvatar && <Avatar size="xs" src={avatar} letters={label} />}
          <Text variant="labelSm" weight="regular">
            {label}
          </Text>
        </div>
      </MenuItemContent>
      {isSelected && iconOnSelected && (
        <ListItemIcon sx={{ minWidth: '20px' }}>
          <Check color={palette.fg.brandSecondary} size={20} />
        </ListItemIcon>
      )}
    </MenuItem>
  );
};
